/**
 * F45 (Wave 2D) — churn page.
 *
 * Route: `/analytics/churn`. Permission: `analytics:read`.
 *
 * There is no dedicated churn read endpoint at MVP — churn surfaces as the two
 * `subscription_churn` + `b2c_churn` KPIs on the dashboard rollup. This page
 * reads the dashboard for the active range and renders the two churn rates as
 * Stat cards + a comparison Bar + a two-row rates Table. There is NO
 * suspended-by-admin metric in the rollup — the page deliberately does not
 * render a hardcoded-0 "Suspended by admin" row (the churn-suspended-reason-stub
 * audit fix); a `suspended` row returns when a real metric ships. The table is
 * titled as the two churn RATES, not a causal reason breakdown. AGGREGATES
 * only — no PII (D15).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chart,
  DateRangePicker,
  Skeleton,
  Stat,
  Table,
  type DateRange,
  type TableColumn,
} from '@bravemove/ui-web';
import type { AnalyticsDashboard, AnalyticsKpiCard } from '@bravemove/types';
import { adminAnalyticsApi } from '../../api/analytics';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';
import { defaultAnalyticsRange, roundPercent, type AnalyticsRangeValue } from './analyticsHelpers';

interface ReasonRow {
  readonly key: string;
  readonly label: string;
  readonly value: number;
}

function kpiValue(kpis: readonly AnalyticsKpiCard[], key: string): number {
  return kpis.find((k) => k.key === key)?.value ?? 0;
}

export function AnalyticsChurnPage() {
  const { t, locale } = useI18n();

  const [range, setRange] = useState<AnalyticsRangeValue>(() => defaultAnalyticsRange());
  const [dashboard, setDashboard] = useState<AnalyticsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDashboard(await adminAnalyticsApi.getDashboard({ from: range.from, to: range.to }));
    } catch {
      setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDashboard();
  }, [fetchDashboard]);

  const subscriptionChurn = dashboard ? kpiValue(dashboard.kpis, 'subscription_churn') : 0;
  const b2cChurn = dashboard ? kpiValue(dashboard.kpis, 'b2c_churn') : 0;

  const reasonRows = useMemo<readonly ReasonRow[]>(
    () => [
      {
        key: 'cancelled',
        label: t('admin:analytics.churn.reasonCancelled'),
        value: subscriptionChurn,
      },
      { key: 'inactive', label: t('admin:analytics.churn.reasonInactive'), value: b2cChurn },
      // No 'suspended' row: there is no backing rollup metric for
      // suspension-driven churn — a hardcoded-0 row would permanently lie.
    ],
    [t, subscriptionChurn, b2cChurn],
  );

  const columns = useMemo<readonly TableColumn<ReasonRow>[]>(
    () => [
      {
        key: 'rate',
        // Honest title: the rows ARE the two churn-rate KPIs relabeled, not a
        // causal reason breakdown. (defaultValue pending the embargoed
        // admin.json `analytics.churn.ratesTableTitle` key.)
        header: t('admin:analytics.churn.ratesTableTitle', { defaultValue: 'Churn rates' }),
        cell: (r) => r.label,
      },
      {
        key: 'value',
        header: '%',
        align: 'end',
        cell: (r) => <span className="font-mono tabular-nums">{roundPercent(r.value)}%</span>,
      },
    ],
    [t],
  );

  const onCustomRange = (dr: DateRange) =>
    setRange((prev) => ({ from: dr.start ?? prev.from, to: dr.end ?? prev.to }));

  return (
    <div className="space-y-6" data-testid="analytics-churn-page">
      <PageHeader eyebrow="Analytics" title={t('admin:analytics.churn.title')} />
      <AnalyticsNav />

      <DateRangePicker
        value={{ start: range.from, end: range.to }}
        onChange={onCustomRange}
        locale={locale}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading || !dashboard ? (
        <Skeleton className="h-72 w-full rounded-xl" data-testid="analytics-churn-loading" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="analytics-churn-kpis">
            <Stat label={t('admin:analytics.churn.b2c')} value={`${roundPercent(b2cChurn)}%`} />
            <Stat
              label={t('admin:analytics.churn.subscription')}
              value={`${roundPercent(subscriptionChurn)}%`}
            />
          </div>
          <section className="rounded-xl border border-hairline bg-surface p-4">
            <Chart
              kind="bar"
              data={[
                { reason: t('admin:analytics.churn.reasonCancelled'), value: subscriptionChurn },
                { reason: t('admin:analytics.churn.reasonInactive'), value: b2cChurn },
              ]}
              xKey="reason"
              series={[{ key: 'value' }]}
              height={240}
            />
          </section>
          <Table
            data={reasonRows as ReasonRow[]}
            columns={columns as TableColumn<ReasonRow>[]}
            rowKey={(r) => r.key}
            density="compact"
          />
        </>
      )}
    </div>
  );
}

export default AnalyticsChurnPage;
